<script setup lang="ts">
  import { useMagicKeys, whenever } from '@vueuse/core';
  import { computed, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';

  import type { Task } from '@stuut/api/models';
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from '@ui/components/alert-dialog';
  import { Checkbox } from '@ui/components/checkbox';
  import { Label } from '@ui/components/label';
  import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@ui/components/select';
  import { toast } from '@ui/components/sonner';
  import { Textarea } from '@ui/components/textarea';
  import { Timestamp } from '@ui/components/time';

  import { stuut } from '@/api';
  import InvoicePromiseToPayPicker from '@/components/common/InvoicePromiseToPayPicker.vue';
  import { useDisputeStore, useTaskCollectionStore } from '@/store';

  interface Props {
    actionType?: string;
    data?: Record<string, any>;
    show?: boolean;
    task: Task;
  }

  const props = defineProps<Props>();

  const emits = defineEmits<{
    (e: 'close'): void;
    (e: 'complete'): void;
    (e: 'success', task?: Task): void;
  }>();

  const generalOptions = [
    'Resolved outside platform',
    'No action needed',
    'Invoice was paid',
    'No longer relevant',
    'Duplicate task',
    'Other',
  ];

  const qualityOptions = [
    'Analysis or classification issue',
    'Unsatisfactory recommendation',
    'Task not necessary',
    'Duplicate task',
    'Other',
  ];

  const closeReason = ref<string | undefined>();
  const closeReasonNote = ref<string | undefined>();
  const isSubmitting = ref<boolean>(false);
  const clearDateOnDismiss = ref(true);
  const isOpen = computed(() => props.show);
  const route = useRoute();
  const taskCollectionStore = useTaskCollectionStore();
  const disputeStore = useDisputeStore();

  const isPaymentPromise = computed(() => Boolean(props.task?.isPaymentPromise));

  const dismissData = computed(() => {
    if (!isPaymentPromise.value) return props.data;
    return { ...(props.data || {}), clear_promise: clearDateOnDismiss.value };
  });

  watch(
    () => props.show,
    (show) => {
      if (show) clearDateOnDismiss.value = true;
    },
  );

  const AlertTypePropMap = computed(() => ({
    close: {
      description: props.task?.is_group
        ? 'Are you sure you want to close this task and all subtasks?'
        : 'Are you sure you want to close this task?',
      options: generalOptions,
      placeholder: "Optional: leave a note why you're closing this task",
      title: 'Dismiss task',
    },
    mark_inaccurate: {
      description: props.task?.is_group
        ? 'Are you sure you want to close this task and all subtasks and flag them as inaccurate?'
        : 'Are you sure you want to close this task and flag it inaccurate?',
      options: qualityOptions,
      placeholder:
        'Optional: Let us know why this task is inaccurate so we can improve future tasks.',
      title: 'Mark inaccurate task',
    },
  }));

  const alertTypeProps = computed(() => {
    if (!props.actionType) return {};
    return AlertTypePropMap.value?.[props.actionType];
  });

  const dismissNoteData = computed(() => ({
    ...(dismissData.value || {}),
    dismiss_reason: closeReason.value,
    dismiss_note: closeReasonNote.value,
  }));

  const closeTask = async () => {
    isSubmitting.value = true;

    emits('close');

    try {
      const response = await stuut.tasks.close(
        props.task,
        closeReason.value,
        closeReasonNote.value,
        dismissNoteData.value,
      );

      toast.success('Task dismissed.');

      taskCollectionStore.refreshAllTaskTables();

      if (typeof route.name === 'string' && route.name.includes('disputes.dispute')) {
        disputeStore.updateDisputePage();
        taskCollectionStore.launchConfetti();
      } else if (!props.task?.is_subtask) {
        taskCollectionStore.afterCompleteTask();
      }

      emits('success', response);

      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      isSubmitting.value = false;
      emits('complete');
    }
  };

  const markInaccurateTask = async () => {
    isSubmitting.value = true;

    emits('close');

    try {
      const response = await stuut.tasks.markInaccurate(
        props.task,
        closeReason.value,
        closeReasonNote.value,
        dismissNoteData.value,
      );

      toast.success('Task marked inaccurate and dismissed.');

      if (!props.task?.is_subtask) taskCollectionStore.afterCompleteTask();
      taskCollectionStore.refreshAllTaskTables();

      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      isSubmitting.value = false;
      emits('complete');
    }
  };

  const onClose = async () => {
    closeReason.value = undefined;
    closeReasonNote.value = undefined;

    emits('close');
  };

  const onConfirm = async () => {
    switch (props.actionType) {
      case 'close':
        return closeTask();
      case 'mark_inaccurate':
        return markInaccurateTask();
      default:
        return undefined;
    }
  };

  const keys = useMagicKeys();

  whenever(keys.escape, () => {
    if (isOpen.value) emits('close');
  });
</script>

<template>
  <AlertDialog v-model:open="isOpen">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ alertTypeProps?.title }}</AlertDialogTitle>
        <AlertDialogDescription as="div" class="flex flex-col gap-y-4">
          <span v-if="alertTypeProps?.description">{{ alertTypeProps?.description }}</span>

          <template v-if="isPaymentPromise">
            <Label
              v-if="task?.is_group"
              class="flex items-center gap-x-2 px-1 w-fit text-xs font-semibold"
            >
              <Checkbox
                :model-value="clearDateOnDismiss"
                @update:model-value="clearDateOnDismiss = Boolean($event)"
              />
              <span>Clear promise to pay dates for all subtasks</span>
            </Label>

            <div v-else-if="task?.invoice" class="flex flex-col gap-y-4">
              <Label
                v-if="task.invoice.promised_to_pay_date"
                class="flex items-center gap-x-2 px-1 w-fit text-xs font-semibold"
              >
                <Checkbox
                  :model-value="clearDateOnDismiss"
                  @update:model-value="clearDateOnDismiss = Boolean($event)"
                />
                <span>Clear promise to pay date</span>
              </Label>

              <div class="flex items-center ml-1 h-5">
                <InvoicePromiseToPayPicker
                  :invoice="task.invoice"
                  @saved="clearDateOnDismiss = false"
                >
                  <Timestamp
                    :time="task.invoice.promised_to_pay_date"
                    :class="clearDateOnDismiss && 'line-through pointer-events-none'"
                    class="text-xs w-fit"
                    format="MMM D YYYY"
                    fallback="Set Date"
                    date-only
                    :editable="!clearDateOnDismiss"
                  />
                </InvoicePromiseToPayPicker>
              </div>
            </div>
          </template>

          <slot name="description" />

          <Select
            :model-value="closeReason"
            class="border-border"
            @update:model-value="(event) => (closeReason = event)"
          >
            <SelectTrigger class="w-full border-border" clearable>
              <SelectValue placeholder="Select reason" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem v-for="option in alertTypeProps.options" :key="option" :value="option">
                {{ option }}
              </SelectItem>
            </SelectContent>
          </Select>

          <Textarea
            v-if="closeReason"
            v-model="closeReasonNote"
            class="w-full border border-border text-xs"
            :placeholder="alertTypeProps?.placeholder"
          />
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel size="sm" @click="onClose">Cancel</AlertDialogCancel>
        <AlertDialogAction size="sm" @click="onConfirm">Dismiss</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<style scoped></style>
